<script setup lang="ts">
import { seoConfig } from "@/config/seo.config";
import type { AsmaName } from "@/data/asma";

defineProps<{
  name: AsmaName;
  isOpen: boolean;
}>();

const emit = defineEmits<{
  (e: "toggle", id: number): void;
}>();

const buildWhatsAppShareUrl = (name: AsmaName) => {
  const shareText = [
    "Asma ul Husna",
    `${name.arabic} (${name.transliteration})`,
    `Meaning: ${name.meaning}`,
    name.reflection ? `Reflection: ${name.reflection}` : "",
    seoConfig.asma.canonical,
  ]
    .filter(Boolean)
    .join("\n");

  return `https://wa.me/?text=${encodeURIComponent(shareText)}`;
};
</script>

<template>
  <div class="border border-gray-200 dark:border-gray-700 rounded-xl overflow-hidden bg-gray-50/60 dark:bg-gray-800/40">
    <button
      class="w-full flex justify-between items-center px-4 py-3 text-left text-gray-900 dark:text-gray-100"
      @click="emit('toggle', name.id)"
    >
      <span class="font-medium">
        {{ name.transliteration }}
      </span>
      <span class="transition-transform duration-300 text-gray-600 dark:text-gray-300" :class="isOpen ? 'rotate-180' : ''">
        &#9662;
      </span>
    </button>

    <transition
      enter-active-class="transition-all duration-300 ease-out"
      leave-active-class="transition-all duration-200 ease-in"
      enter-from-class="opacity-0 max-h-0"
      enter-to-class="opacity-100 max-h-[500px]"
      leave-from-class="opacity-100 max-h-[500px]"
      leave-to-class="opacity-0 max-h-0"
    >
      <div
        v-if="isOpen"
        class="detail-copy px-4 pb-4 pt-2 border-t border-gray-200 dark:border-gray-700 text-sm text-gray-700 dark:text-gray-300 space-y-3 overflow-hidden"
      >
        <div
          class="text-center text-5xl leading-loose text-gray-900 dark:text-gray-100 font-semibold font-arabic"
        >
          {{ name.arabic }}
        </div>

        <div>
          <span class="font-medium">Meaning:</span>
          {{ name.meaning }}
        </div>

        <div class="text-gray-600 dark:text-gray-400">
          {{ name.reflection }}
        </div>

        <div class="text-gray-600 dark:text-gray-400">
          {{ name.benefit }}
        </div>

        <div v-if="name.memoryTip" class="bg-gray-50 dark:bg-gray-800 p-3 rounded-lg text-gray-700 dark:text-gray-300">
          <span class="font-medium">Yaad rakhne ka tareeqa:</span>
          <div class="mt-1">
            {{ name.memoryTip }}
          </div>
        </div>

        <div class="flex justify-end items-center">
          <a
            :href="buildWhatsAppShareUrl(name)"
            target="_blank"
            rel="noopener noreferrer"
            class="inline-flex items-center gap-1.5 whitespace-nowrap rounded-md px-2 py-1 text-sm font-medium text-gray-700 hover:text-gray-900 dark:text-gray-200 dark:hover:text-gray-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gray-600 dark:focus-visible:ring-gray-300"
            :aria-label="`Share ${name.transliteration} on WhatsApp`"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 24 24"
              fill="currentColor"
              class="h-4 w-4"
              aria-hidden="true"
            >
              <path
                d="M12.04 2C6.55 2 2.1 6.44 2.1 11.93c0 1.76.46 3.48 1.34 5L2 22l5.22-1.37a9.9 9.9 0 0 0 4.82 1.23h.01c5.48 0 9.94-4.44 9.94-9.93S17.53 2 12.04 2Zm5.79 14.1c-.24.67-1.36 1.25-1.89 1.33-.49.07-1.12.1-1.8-.11-.41-.13-.94-.31-1.62-.61-2.85-1.23-4.7-4.1-4.84-4.29-.14-.19-1.16-1.54-1.16-2.94s.73-2.09.99-2.38c.26-.28.57-.35.76-.35h.55c.17 0 .4-.06.62.47.24.58.83 2 .9 2.14.07.14.12.31.02.5-.1.19-.16.31-.31.48-.16.17-.33.38-.47.51-.16.16-.32.33-.14.64.18.31.8 1.32 1.72 2.14 1.18 1.05 2.17 1.38 2.48 1.53.31.16.49.14.67-.08.19-.23.79-.92 1-1.24.21-.31.43-.26.72-.16.3.1 1.86.88 2.18 1.03.31.16.52.24.59.38.07.14.07.81-.17 1.48Z"
              />
            </svg>
            <span>Share Allah's Name</span>
          </a>
        </div>
      </div>
    </transition>
  </div>
</template>

<style scoped>
.detail-copy {
  animation: detail-fade 280ms ease-out 200ms both;
}

@keyframes detail-fade {
  from {
    opacity: 0;
    transform: translateY(4px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}
</style>
